import dayjs from "dayjs"
import {
  useCallback,
  useMemo,
} from "react"
import { useGraphContext } from "../GraphContext"
import { usePolyglot } from "src/contexts"

/*
 * Returns a formatter for time divider labels. Labels are hour-only when each
 * label spans a whole number of hours, otherwise they fall back to the normal
 * short time (HH:MM) display format.
 */
export function useDisplayDividerTime(): (value: string | number) => string {
  const {
    hoursPerDivider,
    dividersPerTimeLabel,
  } = useGraphContext()

  const polyglot = usePolyglot()

  /*
   * Building an Intl.DateTimeFormat is comparatively expensive, so allocate it
   * once per hook evaluation and reuse it across every divider label rather
   * than constructing a new instance on each format call.
   */
  const formatter = useMemo(
    () => {
      const options = (hoursPerDivider * dividersPerTimeLabel) % 1 === 0
        ? { hour: "numeric" as const }
        : { timeStyle: "short" as const }

      return new Intl.DateTimeFormat(
        polyglot.locale(),
        options,
      )
    },
    [
      polyglot,
      hoursPerDivider,
      dividersPerTimeLabel,
    ],
  )

  return useCallback(
    (value: string | number) => formatter.format(dayjs(value).toDate()),
    [formatter],
  )
}
